import raw from 'raw.macro';
import { propTypes, PropertyType, PropertyValue, Property, GLSLType, GLSLProp, GLSLUniformLocations } from './propTypes';

// An identifier that cooresponds to a glsl shader source file (or builtin shader).
export type ShaderId = number;
export type PropIndex = number;

// A parsed line from a glsl source
export interface Line {
  content: string;
  lineNum: number;
  sourceId: ShaderId;
}

// check if a regex matches an entire string
function reMatch(re: RegExp, str: string): boolean {
  const match = str.match(re);
  return match !== null && match[0].length === str.length;
}

// Seperate source content into lines
function parseLines(src: string, sourceId: ShaderId): Line[] {
  return src.split(/\r?\n/)
    .map((line, lineNum) => (
      { content: line, lineNum: lineNum + 1, sourceId: sourceId }
    ))
    .filter((line) =>
      // filter out empty/whitespace lines
      !reMatch(/\s*/, line.content)
    );
}

// An error that occured in parsing a shader
interface ParseError {
  msg: string;
  lineNum: number;
  sourceId: ShaderId;
}

// Dependencies have an identifier that must be unique, as well as a display name
// The id is used in $require and $provide, while the display name is used for the user interface
interface FullDepend {
  id: string;
  name: string;
  // if the dependency is required ($after indicates an optional dependency)
  required: boolean;
}

export class ParsedShader {
  // The name supplied by the shader ($name) or the sourceId if a name was not supplied
  name: string;
  // The description supplied by the shader ($description)
  description: string;
  // The interfaces the shader requires implementations of ($require)
  depends: FullDepend[];
  // The interfaces the shader provides ($provide) (just ids, not full names)
  implements: string[];
  // Other shaders the shader includes ($include)
  includes: string[];
  // Properties the shader defines ($prop)
  properties: Property[];
  // The lines in the shader
  lines: Line[];
  // Errors that occured while parsing
  errors: ParseError[];

  private constructor() {
    this.name = "";
    this.description = "";
    this.depends = [];
    this.implements = [];
    this.includes = [];
    this.properties = [];
    this.lines = [];
    this.errors = [];
  }

  // Parse a shader source. Return the ParsedShader + default property values
  static fromSource(src: string, sourceId: ShaderId): [ParsedShader, PropertyValue[]] {
    let res = new ParsedShader();
    let defaultValues: PropertyValue[] = [];

    res.name = `[source ${sourceId}]`;
    res.depends = [];
    res.implements = [];
    res.includes = [];
    res.properties = [];
    res.errors = [];

    const lines = parseLines(src, sourceId);
    res.lines = lines;
    lines.forEach((line, index) => {
      if (ParsedShader.isCommand(line)) {
        res.handleCommand(index, defaultValues);
      }
    });

    res.simplifyDepends();

    return [res, defaultValues];
  }

  // check if a line is a command
  private static isCommand(line: Line): boolean {
    // a command line has the form // $command args
    return reMatch(/\s*\/\/\s*\$.*/, line.content);
  }

  // parse a command line into its components: [command, arg0, arg1, ...]
  private static parseCommand(line: Line): string[] {
    // remove comment + leading and trailing whitespace
    const content = line.content.replace("//", "").trim();
    let res = [];

    let curComp = "";
    let inQuotes = false;
    for (let i = 0; i < content.length; i++) {
      if (content[i] === "\"") {
        inQuotes = !inQuotes;
      } else if (reMatch(/\s/, content[i]) && !inQuotes) {
        if (!reMatch(/\s*/, curComp)) {
          res.push(curComp);
          curComp = "";
        }
      } else {
        curComp += content[i];
      }
    }
    res.push(curComp);

    return res;
  }

  private addError(line: Line, msg: string) {
    this.errors.push({
      msg: msg,
      lineNum: line.lineNum,
      sourceId: line.sourceId
    });
  }

  // apply a command line
  private handleCommand(lineIndex: number, defaultValues: PropertyValue[]) {
    const line = this.lines[lineIndex];
    const comps = ParsedShader.parseCommand(line);

    switch (comps[0]) {
      case "$name":
        if (comps.length !== 2) {
          this.addError(line, "$name expected 1 argument");
        } else {
          this.name = comps[1];
        }
        break;
      case "$require":
        if (comps.length !== 3) {
          this.addError(line, "$require expected 2 argument");
        } else {
          this.depends.push({id: comps[1], name: comps[2], required: true});
        }
        break;
      case "$after":
        if(comps.length !== 2) {
          this.addError(line, "$after expected 1 argument");
        } else {
          this.depends.push({id: comps[1], name: `$after ${comps[1]}`, required: false});
        }
        break;
      case "$provide":
        if (comps.length !== 2) {
          this.addError(line, "$provide expected 1 argument");
        } else {
          this.implements.push(comps[1]);
        }
        break;
      case "$include":
        if (comps.length !== 2) {
          this.addError(line, "$include expected 1 argument");
        } else {
          this.includes.push(comps[1]);
        }
        break;
      case "$prop":
        this.handlePropCommand(lineIndex, line, comps, defaultValues);
        break;
      case "$description":
        this.handleDescriptionLine(lineIndex);
        break;
      case '$end_description':
        // handleDescriptionLine takes care of this command
        break;
      default:
        this.addError(line, `unknown command ${comps[0]}`);
        break;
    }
  }

  // handle a $description line (and following content until $end_description)
  private handleDescriptionLine(lineIndex: number) {
    let description = "";
    // if we hit an $end_description line
    let endDescription = false;
    do {
      lineIndex++;
      const line = this.lines[lineIndex];
      if(ParsedShader.isCommand(line) && ParsedShader.parseCommand(line)[0] === "$end_description") {
        endDescription = true;
      } else {
        // remove leading "//" from line if present
        if(line.content.startsWith("//")) {
          line.content = line.content.substring(2);
        }
        // add to description
        description += line.content + "\n";
        // comment out line so that it won't be treated as glsl
        line.content = "///" + line.content;
      }
    } while (!endDescription && lineIndex < this.lines.length - 1);

    this.description = description;
  }

  // check if a line is a glsl declaration of a const or uniform
  private isPropLine(line: Line): boolean {
    return reMatch(/\s*(?:uniform|const).*/, line.content);
  }

  // parse a const/uniform declaration line
  private parsePropLine(line: Line): [GLSLProp, string] {
    // a const/uniform declaration line has the form:
    // uniform|const type name[array] = default;
    const contents = line.content.trim();
    const comps = contents.split(/\s+/);

    const isConst = comps[0] === "const";
    const baseType = comps[1];
    // after the type qualifier + type, we can no longer rely on components being space seperated. Instead, just go character by character and look for [] or =
    const remaining = comps.slice(2).join("");
    let i = 0;
    let name = "";
    let array: null | string = null;
    let defaultValue = "";
    // parse name
    while (i < remaining.length && remaining[i] !== '[' && remaining[i] !== '=' && remaining[i] !== ';') {
      name += remaining[i++];
    }
    if (i < remaining.length && remaining[i] === '[') {
      i++;
      array = "";
      while (i < remaining.length && remaining[i] !== ']') {
        array += remaining[i++];
      }
      i++; // skip ]
    }
    if (i < remaining.length && remaining[i] === '=') {
      defaultValue = remaining.slice(i + 1).replace(";", "");
    }

    return [new GLSLProp(isConst, new GLSLType(baseType, array), name), defaultValue];
  }

  // apply a $prop line
  private handlePropCommand(lineIndex: number, line: Line, comps: string[], defaultValues: PropertyValue[]) {
    // get the prop name + kind (slider, color, etc)
    if (comps.length < 3) {
      this.addError(line, "$prop expected at least 2 argument (prop name, prop kind)");
      return;
    }
    const name = comps[1];
    const kind = comps[2];
    // get the number of const/uniform declarations following
    let varDecls: [GLSLProp, string][] = [];
    for (let i = lineIndex + 1; i < this.lines.length && this.isPropLine(this.lines[i]); i++) {
      varDecls.push(this.parsePropLine(this.lines[i]));
      // Comment out the const/uniform declaration line
      // It will be reemitted by the compiler with the passed value
      this.lines[i].content = "// parsed prop removed: " + this.lines[i].content;
    }

    // search propTypes for an entry with matching kind + types that match const/uniform declarations
    let type: PropertyType | null = null;
    
    
    
    for(let p = 0; p < propTypes.length; p++) {
      const propType = propTypes[p];
      
      if (propType.kind !== kind) continue;
      if (propType.type.length !== varDecls.length) continue;

      let varTypesMatch = true;
      for (let i = 0; i < varDecls.length; i++) {
        const varDecl = varDecls[i][0].type;
        const prop = propType.type[i];
        
        if (prop.name !== varDecl.name || (prop.array === null) !== (varDecl.array === null)) {
          varTypesMatch = false;
          break;
        }
        
      }

      if(varTypesMatch) {
        type = propType;
        break;
      }
    };

    if (type === null) {
      this.addError(line, `$prop with kind ${kind} and const/uniform types ${varDecls.map((decl) => decl[0].type.toString()).join(", ")} is invalid (no matching property type found)`);
      return;
    }

    // Get additional args passed to prop
    const values = comps.slice(3);
    let condPropIndex: number | null = null;

    // Check for a $if in args
    for(let i = 0; i < values.length; i++) {
      if(values[i] === "$if") {
        if(i === values.length - 1) {
          this.addError(line, 'property name expected after $if');
          return;
        }
        // extract conditional name
        const condProp = values[i + 1];
        // remove conditional from args
        values.splice(i, 2);
        i -= 2;

        // find index of conditional property in already declared properties
        for(let j = 0; j < this.properties.length; j++) {
          if(this.properties[j].displayName === condProp) {
            condPropIndex = j;
          }
        }

        if(condPropIndex === null) {
          this.addError(line, `cannot find property with name '${condProp}' (conditional for $if)`);
          return;
        }

        // make sure property is a boolean
        const condPropEntry = this.properties[condPropIndex];
        if(condPropEntry.vars.length !== 1 || condPropEntry.vars[0].type.name !== "bool") {
          this.addError(line, `property '${condProp}' is not a boolean, so it cannot be used in a $if`);
          return;
        }
      }
    }

    // get values passed to prop
    if (values.length !== type.values) {
      this.addError(line, `$prop ${kind} expected ${type.values} value arguments`);
      return;
    }

    // construct Property
    const prop = new type.ctor(varDecls.map((decl) => decl[0]), values, condPropIndex);
    prop.displayName = name;
    this.properties.push(prop);

    // parse default value
    defaultValues.push({ 
      value: prop.valFromGLSL(varDecls.map((decl) => decl[1])), 
      forceConst: false
    });
  }

  // Remove any depends that are met by a implements
  private simplifyDepends() {
    this.depends = this.depends.filter((dep) =>
      !(dep.id in this.implements)
    );
  }

  // Emit glsl code for this shader (with properties applied)
  emitGLSL(propVals: PropertyValue[]): string {
    let res = "";
    this.properties.forEach((prop, i) => {
      res += prop.toGLSLDecl(propVals[i]);
    });
    this.lines.forEach((line) => {
      // don't emit comment lines
      if (reMatch(/\s*\/\/.*/, line.content)) return;
      // add #line directive with line from source
      res += `#line ${line.lineNum} ${line.sourceId}\n`;
      res += line.content + "\n";
    });
    return res;
  }

  // Set this shader's uniforms for the given properties
  setGLSLUniforms(propVals: PropertyValue[], gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    this.properties.forEach((prop, i) => {
      prop.setGLSLUniforms(propVals[i], gl, uniformLocs);
    });
  }

  // Check if changing this shader's properties from oldPropVals to newPropVals requires the glsl code to be recompiled
  changeRequiresGLSLCompile(oldPropVals: PropertyValue[], newPropVals: PropertyValue[]): boolean {
    for(let i = 0; i < this.properties.length; i++) {
      if(this.properties[i].changeRequiresCompile(oldPropVals[i], newPropVals[i]))
        return true;
    }

    return false;
  }

  // Set the uniforms for the property at propIndex
  setGLSLUniformsForProp(val: PropertyValue, propIndex: number, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    this.properties[propIndex].setGLSLUniforms(val, gl, uniformLocs);
  }

  // Check if changing the property at propIndex from oldVal to newVal requires the glsl code to be recompiled
  propChangeRequiresGLSLCompile(propIndex: number, oldVal: PropertyValue, newVal: PropertyValue): boolean {
    return this.properties[propIndex].changeRequiresCompile(oldVal, newVal);
  }
}


// Property values for a ParsedShaderSet + keyframes for those properties
export interface PropertyKeyframe {
  time: number;
  val: any;
}
export type PropertyKeyframes = PropertyKeyframe[];

function interpolateKeyframes(keys: PropertyKeyframes, prop: Property, t: number): any {
  if(t <= keys[0].time) {
    return keys[0].val;
  }

  for(let i = 0; i < keys.length - 1; i++) {
    const left = keys[i];
    const right = keys[i + 1];
    if(t >= keys[i].time && t < keys[i + 1].time) {
      const normal_t = (t - left.time) / (right.time - left.time);
      return prop.interpolate(left.val, right.val, normal_t);
    }
  }

  return keys[keys.length - 1].val;
}

export interface PropertyKeyframeSet {
  default: PropertyValue[][];
  keyframes: PropertyKeyframes[][];
}

export type KeyframeIndex = [ShaderId, PropIndex, number];

// return true if a PropertyKeyframeSet contains keyframes
export function keyframeSetContainsFrame(props: PropertyKeyframeSet) {
  for(let s = 0; s < props.keyframes.length; s++) {
    const shader = props.keyframes[s];
    for(let p = 0; p < shader.length; p++) {
      const frames = shader[p];
      if(frames.length > 0) {
        return true;
      }
    }
  }

  return false;
}

// A set of ParsedShader's that are conditionally combined together to produce a final glsl output
export class ParsedShaderSet {
  shaders: ParsedShader[];

  constructor(shaders: ParsedShader[]) {
    this.shaders = shaders;
  }

  // Given an array of shader sources, construct a ParsedShaderSet and default property values for each shader
  static fromSource(src: string[]): [ParsedShaderSet, PropertyKeyframeSet] {
    const parsed = src.map((str, i) => ParsedShader.fromSource(str, i));
    const shaders = parsed.map((parsedRes) => parsedRes[0]);
    const defaultVals = parsed.map((parsedRes) => parsedRes[1]);
    const keyframes = parsed.map((parsedRes) => parsedRes[1].map((prop) => []));

    return [
      new ParsedShaderSet(shaders),
      { default: defaultVals, keyframes }
    ];
  }

  // Get all shaders that provide an implementation of a given dependency
  getProvides(depend: string): ShaderId[] {
    let res = [];
    for(let i = 0; i < this.shaders.length; i++) {
      if(this.shaders[i].implements.includes(depend)) 
        res.push(i);
    }

    return res;
  }

  // From a list of selected shaders, return those that provide an implementation of a given dependency
  getSelectedProvides(selected: ShaderId[], depend: string): ShaderId[] {
    let res = [];
    for(let i = 0; i < selected.length; i++) {
      const shader = selected[i];
      if(this.shaders[shader].implements.includes(depend))
        res.push(shader);
    }

    return res;
  }

  // Given a list of selected shaders, check if a given dependency is met
  private depMet(selected: ShaderId[], depend: string): boolean {
    for(let i = 0; i < selected.length; i++) {
      if(this.shaders[selected[i]].implements.includes(depend)) {
        return true;
      }
    }

    return false;
  }

  // Given a list of selected shaders, return a list of dependencies that need to be met
  getDepends(selectedShaders: ShaderId[], rootShader: ShaderId): FullDepend[] {
    let res: FullDepend[] = [];
    // re-order selected so that dependencies are visited depth-first
    // (so that dependencies appear in a logical order for user)
    const selected = this.removeNonImplementing(selectedShaders, rootShader);

    for(let i = 0; i < selected.length; i++) {
      const shader = this.shaders[selected[i]];
      for(let d = 0; d < shader.depends.length; d++) {
        if(res.findIndex((dep) => dep.id === shader.depends[d].id) === -1 && shader.depends[d].required)
          res.push(shader.depends[d]);
      }
    }

    return res;
  }

  // Given a list of selected shaders, return a list of dependencies that don't have an implementation
  getUnmetDepends(selected: ShaderId[], rootShader: ShaderId): FullDepend[] {
    return this.getDepends(selected, rootShader).filter((depend) => !this.depMet(selected, depend.id) && depend.required);
  }

  // Given a list of selected shaders, remove any that aren't transitively implementing a dependency of rootShader
  removeNonImplementing(selected: ShaderId[], rootShader: ShaderId, res: ShaderId[]=[]): ShaderId[] {
    if(selected.length === 0) return [];

    // Add the root shader to result, removing previous entries if present
    if(res.includes(rootShader)) {
      const index = res.indexOf(rootShader);
      res.splice(index, 1);
    }
    res.push(rootShader);
    // Get dependencies of the root shader
    const rootDeps = this.shaders[rootShader].depends;
    // Visit each shader implementing one of the root shader's dependencies
    rootDeps.forEach(dep => {
      const implementors = this.getSelectedProvides(selected, dep.id);
      // Recursively add dependencies of the implementor
      implementors.forEach(implementor => {
        this.removeNonImplementing(selected, implementor, res);
      });
    });

    return res;
  }

  // Given a list of selected shaders, order them so that implementations are located before shaders that depend on those implementations;
  // We need to do this because glsl doesn't support forward referenced functions
  private orderSelected(selected: ShaderId[], rootShader: ShaderId): ShaderId[] {
    return this.removeNonImplementing(selected, rootShader).reverse();
  }

  // Given a list of selected shaders, return a list of all uniforms that are backing properties.
  // We need to know this in order to know which uniform locations to get when we compile the shader.
  getUniformNames(selected: ShaderId[], props: PropertyValue[][]): string[] {
    let res: string[] = [];
    selected.forEach((select) => {
      this.shaders[select].properties.forEach((prop, propIndex) => {
        prop.vars.forEach((glslVar) => {
          // only include variables that are uniforms
          if(!props[select][propIndex].forceConst && !glslVar.isConst)
            res.push(glslVar.name);
        })
      });
    });

    return res;
  }

  // Given a list of selected shaders and properties to apply to each, generate glsl code
  emitGLSL(selected: ShaderId[], props: PropertyValue[][]): string {
    const ordered = this.orderSelected(selected, 0);
    let res =  raw("../../shaders/preamble.glsl");
    ordered.forEach((select) => {
      res += this.shaders[select].emitGLSL(props[select]);
    });

    return res;
  }

  // Apply uniform properties to the list of selected shader
  setGLSLUniforms(selected: ShaderId[], props: PropertyValue[][], gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    selected.forEach((select) => {
      this.shaders[select].setGLSLUniforms(props[select], gl, uniformLocs);
    });
  }

  // Set the glsl uniforms for the property in shader at propIndex to val
  setGLSLUniformsForProp(val: PropertyValue, shader: ShaderId, propIndex: number, gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations) {
    this.shaders[shader].setGLSLUniformsForProp(val, propIndex, gl, uniformLocs);
  }

  // Check if changing shader's property at propIndex from oldVal to newVal requires the glsl code to be recompiled
  propChangeRequiresGLSLCompile(shader: ShaderId, propIndex: number, oldVal: PropertyValue, newVal: PropertyValue): boolean {
    return this.shaders[shader].propChangeRequiresGLSLCompile(propIndex, oldVal, newVal);
  }

  // Interpolate a PropertyKeyframeSet to produce property values at a specific time
  interpolate(values: PropertyKeyframeSet, t: number): PropertyValue[][] {
    let res: PropertyValue[][] = [];
    for(let shaderId = 0; shaderId < this.shaders.length; shaderId++) {
      res.push([]);
      const props = values.default[shaderId];
      for(let propIndex = 0; propIndex < this.shaders[shaderId].properties.length; propIndex++) {
        const keys = values.keyframes[shaderId][propIndex];
        // If there are no keyframes on this prop use its default value
        if(keys.length === 0) {
          res[shaderId].push(props[propIndex]);
        } 
        // otherwise interpolate
        else {
          res[shaderId].push({
            value: interpolateKeyframes(keys, this.shaders[shaderId].properties[propIndex], t), 
            forceConst: props[propIndex].forceConst
          });
        }
      }
    }

    return res;
  }

  // Interpolate a specific value from a PropertyKeyFrameSet
  interpolateValue(values: PropertyKeyframeSet, shaderId: ShaderId, propIndex: PropIndex, t: number): PropertyValue {
    const defaultVal = values.default[shaderId][propIndex];
    const keys = values.keyframes[shaderId][propIndex];

    if(keys.length === 0) {
      return defaultVal;
    } else {
      return {
        value: interpolateKeyframes(keys, this.shaders[shaderId].properties[propIndex], t),
        forceConst: defaultVal.forceConst
      };
    }
  }


  // Given a list of selected shaders + shader properties, generate a js object suitable for serialization that represents the selected shaders + properties independent of file ordering
  serializePropVals<T>(shaderFilenames: string[], selected: ShaderId[], props: T[][]): any {
    let res: any = {};
    selected.forEach((shaderId) => {
      // add filename entry for each selected shader
      const name = shaderFilenames[shaderId];
      const shader = this.shaders[shaderId];
      res[name] = {};
      // add all prop values to entry
      for(const index in props[shaderId]) {
        const prop = shader.properties[index];
        res[name][prop.displayName] = props[shaderId][index];
      }
    });

    return res;
  }

  // Serialize a keyframe set
  serializeKeyframeSet(shaderFilenames: string[], selected: ShaderId[], keyframes: PropertyKeyframeSet): any {
    return {
      default: this.serializePropVals(shaderFilenames, selected, keyframes.default),
      keyframes: this.serializePropVals(shaderFilenames, selected, keyframes.keyframes),
    };
  }

  // Given a object generated by serializePropVals, apply it on curProps and return the result and the selected shaders
  deserializePropVals<T>(obj: any, shaderFilenames: string[], curProps: T[][]): [T[][], ShaderId[]] {
    let selected: ShaderId[] = [];
    let newProps = JSON.parse(JSON.stringify(curProps)) as T[][];
    for(const filename in obj) {
      // find shader id with this entry's filename
      const shaderId = shaderFilenames.indexOf(filename);
      if(shaderId === -1) continue;
      selected.push(shaderId);

      const shader = this.shaders[shaderId];
      const props = obj[filename];
      for(const propName in props) {
        // find prop with this entry's name
        const propIndex = shader.properties.findIndex((prop) => prop.displayName === propName);
        if(propIndex === -1) continue;

        newProps[shaderId][propIndex] = props[propName];
      }
    }

    return [newProps, selected];
  }

  // Deserialize a keyframe set
  deserializeKeyframeSet(obj: any, shaderFilenames: string[], curKeys: PropertyKeyframeSet): [PropertyKeyframeSet, ShaderId[]] {
    // If there aren't "default" or "keyframes" entries, try to deserialize as just a default array.
    // This is to support fractals created before keyframes were added
    if(!("default" in obj) || !("keyframes" in obj)) {
      const [defaultVals, selected] = this.deserializePropVals(obj, shaderFilenames, curKeys.default);
      return [{ default: defaultVals, keyframes: curKeys.keyframes }, selected];
    }

    const [defaultVals, selected] = this.deserializePropVals(obj.default, shaderFilenames, curKeys.default);
    const keyframes = this.deserializePropVals(obj.keyframes, shaderFilenames, curKeys.keyframes)[0];

    return [{ default: defaultVals, keyframes }, selected];
  }

  // Get a list of all errors that occurred during parsing
  getErrors(): ParseError[] {
    return this.shaders.map((shader) => shader.errors).flat();
  }
}

